import { FutureRelease, TransactionFormData } from '@/types';

export class RecurrenceProcessor {
  static createTransactionFromRelease(
    release: FutureRelease,
    dueDate: string,
    addTransaction: (data: TransactionFormData) => Promise<any>
  ): Promise<any> {
    const transactionData: TransactionFormData = {
      description: release.name,
      amount: Math.abs(release.amount),
      categoryId: release.categoryId || '',
      date: dueDate,
      type: release.type,
      paymentMethod: release.paymentMethod || 'pix',
      accountId: release.accountId || '',
      cardId: release.cardId || '',
      account: '', // Will be resolved by the transaction system
      status: 'pending' // Mark as pending since it's automatically generated
    };

    console.log(`[RecurrenceProcessor] Criando transação para ${release.name} em ${dueDate}:`, transactionData);
    return addTransaction(transactionData);
  }

  static shouldProcessTodaysRelease(release: FutureRelease, today: string): boolean {
    console.log(`[RecurrenceProcessor] Verificando release: ${release.name} para ${today}`);
    
    // Pular se cancelado
    if (release.status === 'cancelado') {
      console.log(`[RecurrenceProcessor] Release cancelado: ${release.name}`);
      return false;
    }
    
    // Pular se já foi pago e não é recorrente
    if (release.status === 'pago' && !release.isRecurrent) {
      console.log(`[RecurrenceProcessor] Release já pago e não recorrente: ${release.name}`);
      return false;
    }
    
    // Para recorrentes, processar se a data de vencimento já passou (incluindo hoje)
    // Para únicos, processar apenas se for exatamente hoje
    const isDueOrPast = release.isRecurrent ? release.dueDate <= today : release.dueDate === today;
    if (!isDueOrPast) {
      return false;
    }
    
    // Verificar se já foi processado hoje (com verificação mais robusta)
    const lastProcessed = (release as any).lastProcessedDate;
    const notAlreadyProcessed = !lastProcessed || lastProcessed !== today;
    
    // Para lançamentos recorrentes, verificar se não foi processado hoje
    if (release.isRecurrent && lastProcessed === today) {
      console.log(`[RecurrenceProcessor] Release recorrente já processado hoje: ${release.name}`);
      return false;
    }
    
    // Verificar se está dentro do período de recorrência
    const withinRecurrencePeriod = !release.recurrenceEndDate || today <= release.recurrenceEndDate;
    
    const shouldProcess = notAlreadyProcessed && withinRecurrencePeriod;
    
    console.log(`[RecurrenceProcessor] Release ${release.name}: isDueOrPast=${isDueOrPast}, notAlreadyProcessed=${notAlreadyProcessed}, withinRecurrencePeriod=${withinRecurrencePeriod}, shouldProcess=${shouldProcess}`);
    
    return shouldProcess;
  }

  static calculateNextDueDate(release: FutureRelease): string {
    if (!release.isRecurrent || !release.recurrence || release.recurrence === 'unica') {
      return release.dueDate;
    }

    const currentDate = new Date(release.dueDate + 'T12:00:00');
    
    switch (release.recurrence) {
      case 'semanal':
        currentDate.setDate(currentDate.getDate() + 7);
        break;
      case 'mensal':
        currentDate.setMonth(currentDate.getMonth() + 1);
        break;
      case 'bimestral':
        currentDate.setMonth(currentDate.getMonth() + 2);
        break;
      case 'trimestral':
        currentDate.setMonth(currentDate.getMonth() + 3);
        break;
      case 'semestral':
        currentDate.setMonth(currentDate.getMonth() + 6);
        break;
      case 'anual':
        currentDate.setFullYear(currentDate.getFullYear() + 1);
        break;
      default:
        return release.dueDate;
    }

    return currentDate.toISOString().split('T')[0];
  }

  static generateFutureTransactionPredictions(
    release: FutureRelease,
    startDate: string,
    endDate: string,
    maxPredictions: number = 365,
    referenceDate?: Date // Add reference date for period navigation
  ): Array<{ date: string; amount: number; description: string }> {
    if (!release.isRecurrent || !release.recurrence || release.recurrence === 'unica') {
      // Para lançamentos únicos, retornar apenas se estiver no período
      if (release.dueDate >= startDate && release.dueDate <= endDate && release.status !== 'pago') {
        return [{
          date: release.dueDate,
          amount: release.amount,
          description: release.name
        }];
      }
      return [];
    }

    const predictions: Array<{ date: string; amount: number; description: string }> = [];
    let currentDate = new Date(release.dueDate + 'T12:00:00');
    let count = 0;

    // Use referenceDate instead of today for period navigation
    const referenceString = referenceDate ? referenceDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    
    console.log(`[RecurrenceProcessor] Generating predictions for ${release.name}:`, {
      originalDueDate: release.dueDate,
      referenceString,
      startDate,
      endDate
    });
    
    // Calculate all occurrences from the original due date forward
    let tempRelease = { ...release };
    
    // If the original due date is before the start date, advance to find the first occurrence >= startDate
    while (tempRelease.dueDate < startDate) {
      const nextDate = this.calculateNextDueDate(tempRelease);
      if (nextDate === tempRelease.dueDate) break; // Prevent infinite loop
      tempRelease.dueDate = nextDate;
    }
    
    currentDate = new Date(tempRelease.dueDate + 'T12:00:00');
    
    console.log(`[RecurrenceProcessor] Starting from calculated date: ${tempRelease.dueDate}`);

    while (count < maxPredictions) {
      const dateStr = currentDate.toISOString().split('T')[0];
      
      // Parar se ultrapassou a data final da recorrência
      if (release.recurrenceEndDate && dateStr > release.recurrenceEndDate) {
        break;
      }
      
      // Parar se ultrapassou o período de previsão
      if (dateStr > endDate) {
        break;
      }

      // Adicionar se estiver no período de interesse
      if (dateStr >= startDate) {
        predictions.push({
          date: dateStr,
          amount: release.amount,
          description: release.name
        });
      }

      // Calcular próxima ocorrência
      switch (release.recurrence) {
        case 'semanal':
          currentDate.setDate(currentDate.getDate() + 7);
          break;
        case 'mensal':
          currentDate.setMonth(currentDate.getMonth() + 1);
          break;
        case 'bimestral':
          currentDate.setMonth(currentDate.getMonth() + 2);
          break;
        case 'trimestral':
          currentDate.setMonth(currentDate.getMonth() + 3);
          break;
        case 'semestral':
          currentDate.setMonth(currentDate.getMonth() + 6);
          break;
        case 'anual':
          currentDate.setFullYear(currentDate.getFullYear() + 1);
          break;
      }
      
      count++;
    }

    console.log(`[RecurrenceProcessor] Generated ${predictions.length} predictions for ${release.name}:`, predictions.map(p => p.date));
    return predictions;
  }
}